import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Send } from "lucide-react";
import Reveal from "@/components/Reveal";

/* eslint-disable react/no-unescaped-entities */

// ─────────────────────────────────────────────────────────────
// Confidentialite — Politique de confidentialité / RGPD
// ─────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
    { v: "access", l: "Droit d'accès" },
    { v: "rectification", l: "Droit de rectification" },
    { v: "opposition", l: "Droit d'opposition" },
    { v: "erasure", l: "Droit à l'effacement" },
    { v: "limitation", l: "Droit à la limitation" },
    { v: "portability", l: "Droit à la portabilité" },
    { v: "other", l: "Autre demande" },
];

export default function Confidentialite() {
    const [legal, setLegal] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/api/public/legal`)
            .then(({ data }) => setLegal(data.legal))
            .catch(() => {});
    }, []);

    return (
        <div data-testid="confidentialite-page" className="bg-xv-ivory">
            {/* HERO */}
            <section className="bg-xv-midnightDeep text-xv-ivory pt-32 md:pt-40 pb-20 md:pb-24">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-8 text-xv-goldSoft">
                            <span className="block h-px w-12 bg-xv-goldSoft" />
                            <span className="overline">
                                Politique de confidentialité
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-ivory text-[36px] sm:text-5xl lg:text-[60px] leading-[1.04] tracking-tight max-w-3xl"
                            style={{ fontWeight: 300 }}
                        >
                            Vos données personnelles. Notre engagement.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-9 text-xv-ivory/70 text-[15px] md:text-[16px] leading-[1.9] font-light max-w-2xl">
                            Conformément au Règlement (UE) 2016/679 (RGPD) et à
                            la loi Informatique et Libertés, nous garantissons
                            la confidentialité, la sécurité et le respect des
                            droits liés à vos données personnelles.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-xv-ivory py-20 md:py-28">
                <div className="max-w-[920px] mx-auto px-6 md:px-12 space-y-14">
                    <Block title="1. Finalités des traitements">
                        <Paragraph>
                            Les données personnelles collectées sur ce site sont
                            traitées pour : répondre aux demandes d'entretien,
                            transmettre des informations à votre demande,
                            assurer le suivi des relations professionnelles,
                            répondre aux obligations légales et fiscales, et
                            améliorer l'expérience de navigation.
                        </Paragraph>
                    </Block>

                    <Block title="2. Données collectées">
                        <Paragraph>
                            Nous ne collectons que les données strictement
                            nécessaires : nom, prénom, fonction, société,
                            adresse e-mail, téléphone, ainsi que le contenu
                            des messages que vous nous adressez.
                        </Paragraph>
                        <Paragraph>
                            Aucune donnée sensible (origine, religion,
                            opinions, santé…) n'est collectée.
                        </Paragraph>
                    </Block>

                    <Block title="3. Formulaires de contact">
                        <Paragraph>
                            Les formulaires de contact présents sur le site
                            transmettent vos demandes par voie sécurisée au
                            cabinet. Aucune donnée n'est cédée à des tiers à
                            des fins commerciales.
                        </Paragraph>
                    </Block>

                    <Block title="4. Cookies">
                        <Paragraph>
                            Le site utilise uniquement des cookies techniques
                            strictement nécessaires à son fonctionnement.
                            Aucun traceur publicitaire, aucun outil d'analyse
                            comportementale tiers.
                        </Paragraph>
                    </Block>

                    <Block title="5. Base légale des traitements">
                        <Paragraph>
                            Les traitements reposent sur l'intérêt légitime du
                            cabinet à répondre aux demandes reçues, sur
                            l'exécution des relations contractuelles, et — le
                            cas échéant — sur le consentement explicite de
                            l'utilisateur.
                        </Paragraph>
                    </Block>

                    <Block title="6. Durée de conservation">
                        <Paragraph>
                            Données de prospection : 3 ans à compter du
                            dernier contact. Données contractuelles : durée
                            de la relation augmentée des délais légaux de
                            conservation. Données comptables : 10 ans.
                        </Paragraph>
                    </Block>

                    <Block title="7. Destinataires des données">
                        <Paragraph>
                            Les données sont strictement réservées aux
                            membres habilités du cabinet. Aucune transmission
                            à des tiers, hors prestataires techniques liés par
                            une obligation contractuelle de confidentialité
                            (hébergeur, messagerie sécurisée).
                        </Paragraph>
                    </Block>

                    <Block title="8. Sécurité des données">
                        <Paragraph>
                            Les données sont stockées sur des serveurs
                            sécurisés, protégées par des mesures techniques
                            et organisationnelles appropriées :
                            chiffrement, contrôle d'accès strict, sauvegardes
                            chiffrées, journalisation des accès.
                        </Paragraph>
                    </Block>

                    <Block title="9. Vos droits">
                        <Paragraph>
                            Conformément au RGPD, vous disposez des droits
                            suivants sur vos données personnelles :
                        </Paragraph>
                        <ul className="space-y-2.5 mt-3 text-[15px] text-xv-text-secondary leading-relaxed">
                            {[
                                "Droit d'accès — obtenir copie de vos données",
                                "Droit de rectification — corriger des informations erronées",
                                "Droit d'opposition — refuser certains traitements",
                                "Droit à l'effacement — supprimer vos données",
                                "Droit à la limitation — restreindre l'usage",
                                "Droit à la portabilité — récupérer vos données",
                            ].map((r) => (
                                <li key={r} className="flex items-start gap-3">
                                    <Check
                                        strokeWidth={1.4}
                                        size={14}
                                        className="text-xv-gold mt-1 shrink-0"
                                    />
                                    <span className="font-light">{r}</span>
                                </li>
                            ))}
                        </ul>
                        <Paragraph>
                            Vous pouvez exercer ces droits à tout moment via
                            le formulaire ci-dessous ou par courriel à&nbsp;
                            <a
                                href={`mailto:${legal?.dpo_email || "contact@xv-capitalpartners.com"}`}
                                className="link-underline text-xv-midnight"
                            >
                                {legal?.dpo_email || "contact@xv-capitalpartners.com"}
                            </a>
                            .
                        </Paragraph>
                    </Block>

                    {/* RGPD FORM */}
                    <RGPDForm legal={legal} />
                </div>
            </section>
        </div>
    );
}

function RGPDForm({ legal }) {
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        request_type: "access",
        message: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await axios.post(
                `${API}/api/public/legal/rgpd-request`,
                form,
            );
            setDone(true);
        } catch (e) {
            setErr(
                e?.response?.data?.detail
                || "Une erreur est survenue. Réessayez ou contactez-nous par e-mail.",
            );
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <Reveal>
                <section
                    data-testid="rgpd-success"
                    className="bg-xv-cream border-l-2 border-xv-gold p-8 md:p-10"
                >
                    <div className="overline text-xv-gold mb-4">
                        Demande enregistrée
                    </div>
                    <h3
                        className="font-serif text-xv-black text-[22px] md:text-[26px] mb-4 leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Votre demande nous est parvenue.
                    </h3>
                    <p className="text-[15px] text-xv-text-secondary leading-relaxed font-light">
                        Nous vous répondrons dans un délai d'un mois à l'adresse
                        indiquée. Un courriel de confirmation a été envoyé à
                        notre Délégué à la protection des données.
                    </p>
                </section>
            </Reveal>
        );
    }

    return (
        <Reveal>
            <section
                data-testid="rgpd-form-section"
                className="bg-xv-cream p-8 md:p-10 lg:p-12"
            >
                <div className="overline text-xv-gold mb-4">
                    Exercer mes droits
                </div>
                <h3
                    className="font-serif text-xv-black text-[24px] md:text-[28px] leading-tight mb-3"
                    style={{ fontWeight: 400 }}
                >
                    Formulaire d'exercice des droits RGPD.
                </h3>
                <p className="text-[14px] text-xv-text-secondary leading-relaxed font-light italic mb-8 max-w-xl">
                    Nous traitons toutes les demandes dans un délai d'un mois.
                    Une pièce d'identité peut vous être demandée.
                </p>
                <form
                    data-testid="rgpd-form"
                    onSubmit={submit}
                    className="space-y-6"
                >
                    <Field
                        label="Nom complet"
                        required
                        value={form.full_name}
                        onChange={(e) =>
                            setForm({ ...form, full_name: e.target.value })
                        }
                        testid="rgpd-name"
                    />
                    <Field
                        label="Adresse e-mail"
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) =>
                            setForm({ ...form, email: e.target.value })
                        }
                        testid="rgpd-email"
                    />
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-xv-gold/85 mb-3">
                            Nature de la demande
                        </label>
                        <select
                            data-testid="rgpd-type"
                            value={form.request_type}
                            onChange={(e) =>
                                setForm({ ...form, request_type: e.target.value })
                            }
                            className="w-full bg-transparent border-b border-xv-black/20 focus:border-xv-gold outline-none py-3 text-[15px] font-serif text-xv-black"
                        >
                            {REQUEST_TYPES.map((r) => (
                                <option key={r.v} value={r.v}>
                                    {r.l}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-xv-gold/85 mb-3">
                            Précisez votre demande
                        </label>
                        <textarea
                            data-testid="rgpd-message"
                            required
                            minLength={10}
                            value={form.message}
                            onChange={(e) =>
                                setForm({ ...form, message: e.target.value })
                            }
                            rows={5}
                            placeholder="Décrivez précisément votre demande…"
                            className="w-full bg-transparent border border-xv-black/15 focus:border-xv-gold outline-none p-4 text-[15px] font-serif text-xv-black leading-relaxed resize-y placeholder-xv-text-secondary/50"
                        />
                    </div>
                    {err && (
                        <div className="text-[13px] text-red-700 border-l-2 border-red-700 pl-3 py-1">
                            {err}
                        </div>
                    )}
                    <div className="pt-4">
                        <button
                            type="submit"
                            data-testid="rgpd-submit"
                            disabled={busy}
                            className="group inline-flex items-center gap-4 px-9 py-4 border border-xv-black text-xv-black text-[11px] tracking-[0.3em] uppercase hover:bg-xv-black hover:text-xv-ivory transition-all duration-700 disabled:opacity-40"
                        >
                            {busy ? "Envoi en cours…" : "Envoyer ma demande"}
                            <Send
                                strokeWidth={1.2}
                                size={14}
                                className="transition-transform duration-500 group-hover:translate-x-1"
                            />
                        </button>
                    </div>
                </form>
            </section>
        </Reveal>
    );
}

function Block({ title, children }) {
    return (
        <Reveal>
            <section>
                <h2
                    className="font-serif text-xv-black text-[22px] md:text-[26px] leading-tight mb-6 pb-3 border-b border-xv-line"
                    style={{ fontWeight: 400 }}
                >
                    {title}
                </h2>
                <div className="space-y-4">{children}</div>
            </section>
        </Reveal>
    );
}

function Paragraph({ children }) {
    return (
        <p className="text-[15px] md:text-[16px] text-xv-text-secondary leading-[1.9] font-light">
            {children}
        </p>
    );
}

function Field({ label, testid, ...props }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-xv-gold/85 mb-3">
                {label}
            </label>
            <input
                {...props}
                data-testid={testid}
                className="w-full bg-transparent border-b border-xv-black/20 focus:border-xv-gold outline-none py-3 text-[15px] font-serif text-xv-black"
            />
        </div>
    );
}
